/* The last write the server refused for being over the plan — STORY-045.
 *
 * ── why this exists at all ───────────────────────────────────────────────────
 * The lock is enforced on the server and works. What was missing was anybody
 * being told: four screens knew how to render `entitlement_exceeded`, and all
 * four were CREATE paths that already had a limit refusal of their own. Editing
 * an Event, or a setting, or an inventory item got a 403 that nothing displayed,
 * so the change simply appeared not to happen.
 *
 * Reported as "it doesn't work, I can still change everything" on 2026-09-05 —
 * against a workspace where every one of those writes was in fact being
 * refused. A silent refusal and no refusal look identical from a chair.
 *
 * ── one handler, not one per form ────────────────────────────────────────────
 * Registered on the query client's mutation cache, so it covers every mutation
 * in the product and every one added later. The alternative — teaching each
 * form to recognise the code — is the same shape of mistake as guarding each
 * route by hand: it works until somebody forgets, and forgetting is silent.
 *
 * It deliberately holds only the FACT of a refusal, not a message. The banner
 * already on screen says which meters are over and by how much; repeating that
 * here would be a second copy to drift.
 */

type Listener = () => void;

let refusedAt: number | null = null;
const listeners = new Set<Listener>();

/** Records that a write was refused. Called from the mutation cache. */
export function recordRefusal(): void {
  refusedAt = Date.now();
  for (const listener of listeners) listener();
}

/** Clears it — on navigation, or once the workspace fits again. */
export function clearRefusal(): void {
  if (refusedAt === null) return;
  refusedAt = null;
  for (const listener of listeners) listener();
}

export function subscribeToRefusals(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Snapshot for `useSyncExternalStore`.
 *
 * Returns the timestamp rather than a boolean so a SECOND refusal is a new
 * value: with a boolean, refusing twice in a row would not re-render and the
 * message would look stale to somebody who just tried again.
 */
export const refusalSnapshot = (): number | null => refusedAt;
